import React from 'react';
import translate from '@data/translations';
import dayjs from 'dayjs';
import numbro from 'numbro';
import {
    ClientTypeEnum,
    InvoiceConclusionItemModel,
    InvoiceConclusionModel,
    IssuerCompanyInfo,
    InvoiceConclusionDetailsModel,
} from '@common/modelDefinition';
import useStore from '@data/state/zustand';
import { getGroupedInvoiceConclusionItemsByClientType } from './utils';
import { getInvoiceConclusionDetails } from './api';

export enum InvoiceTypeEnumText {
    'Geniune invoices',
    'Advance invoices',
    'Proforma invoices',
    'Proforma dispatched invoices',
    'Processed advance invoices' = 9,
}

// Konstanta za proformaDispatched tip
const PROFORMA_DISPATCHED_TYPE = 3;

interface InvoiceConclusionReportProps {
    invoiceConclusion: InvoiceConclusionModel;
    companyInfo: IssuerCompanyInfo;
}

function InvoiceConclusionReport(props: InvoiceConclusionReportProps) {
    const { invoiceConclusion, companyInfo } = props;
    const defaultCurrency: string = useStore((appState: any) => appState.defaultCurrency);
    const systemLocale: string = useStore((appState: any) => appState.locale);

    // Dohvati details za popuste
    const [conclusionDetailsForReport, setConclusionDetailsForReport] = React.useState<InvoiceConclusionDetailsModel[] | null>(null);

    React.useEffect(() => {
        const loadDetails = async () => {
            if (invoiceConclusion?.id && invoiceConclusion?.posTerminalId) {
                try {
                    const details = await getInvoiceConclusionDetails({
                        posTerminalId: invoiceConclusion.posTerminalId,
                        invoiceConclusionId: invoiceConclusion.id,
                        group: 'plu',
                    });
                    setConclusionDetailsForReport(details.invoiced || []);
                } catch (error) {
                    console.log('Error fetching conclusion details:', error);
                    setConclusionDetailsForReport(null);
                }
            }
        };
        loadDetails();
    }, [invoiceConclusion?.id, invoiceConclusion?.posTerminalId]);

    const groupedInvoiceConclusionItemsByClientType = getGroupedInvoiceConclusionItemsByClientType(
        invoiceConclusion?.InvoiceConclusionItems ? invoiceConclusion.InvoiceConclusionItems : []
    );

    // Izračunaj ukupne vrijednosti za sve stavke OSIM proformaDispatched i obrađenih avanasa (invoiceType 9)
    const totalSummary = (invoiceConclusion?.InvoiceConclusionItems || [])
        .filter((item) => 
            Number(item.invoiceType) !== PROFORMA_DISPATCHED_TYPE && 
            Number(item.invoiceType) !== 9
        )
        .reduce(
            (acc, item) => {
                acc.cash += item.cash || 0;
                acc.card += item.card || 0;
                acc.virman += item.virman || 0;
                acc.cashTotal += item.cashTotal || 0;
                acc.nonCashTotal += item.nonCashTotal || 0;
                acc.total += item.total || 0;
                acc.invoiceCount += item.invoiceCount || 0;
                acc.stornoInvoiceCount += item.stornoInvoiceCount || 0;
                return acc;
            },
            {
                cash: 0,
                card: 0,
                virman: 0,
                cashTotal: 0,
                nonCashTotal: 0,
                total: 0,
                invoiceCount: 0,
                stornoInvoiceCount: 0,
            }
        );

    // Izračunaj ukupne vrijednosti SAMO za proformaDispatched (otpremnice)
    const proformaDispatchedSummary = (invoiceConclusion?.InvoiceConclusionItems || [])
        .filter((item) => Number(item.invoiceType) === PROFORMA_DISPATCHED_TYPE)
        .reduce(
            (acc, item) => {
                acc.total += item.proformaDispatched || 0;
                acc.invoiceCount += item.invoiceCount || 0;
                acc.stornoInvoiceCount += item.stornoInvoiceCount || 0;
                return acc;
            },
            {
                total: 0,
                invoiceCount: 0,
                stornoInvoiceCount: 0,
            }
        );

    const hasProformaDispatched = proformaDispatchedSummary.invoiceCount > 0 || proformaDispatchedSummary.stornoInvoiceCount > 0 || proformaDispatchedSummary.total > 0;

    // Izračunaj popuste iz conclusionDetailsForReport (group='plu')
    const filteredDetails = conclusionDetailsForReport || [];
    const detailsTotalWithoutDiscount = filteredDetails.reduce(
        (sum, detail) => sum + Number(detail.totalValue || 0), 0
    );
    const detailsTotalWithDiscount = filteredDetails.reduce(
        (sum, detail) => sum + Number(detail.totalValueWithDiscount || 0), 0
    );
    const totalDiscount = detailsTotalWithoutDiscount - detailsTotalWithDiscount;
    const hasDiscountData = conclusionDetailsForReport && conclusionDetailsForReport.length > 0 && totalDiscount > 0;

    return (
        <div className="">
            <div className="col-12 mt-3">
                <h5 className="mt-3 pointer text-center uppercase p-2">
                    {<b>{translate('Closing revenue report')}</b>}{' '}
                </h5>
            </div>
            <div className="m-auto" style={{ width: '900px' }}>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Company name')}</div>
                    <div className="col-6 text-right">{companyInfo.name}</div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Id number')}</div>
                    <div className="col-6 text-right">{companyInfo.idNumber}</div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Address')}</div>
                    <div className="col-6 text-right">{companyInfo.address}</div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Post office number')}</div>
                    <div className="col-6 text-right">
                        {companyInfo.postOfficeNumber} {companyInfo.town}
                    </div>
                </div>
                <br />

                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Document type')}</div>
                    <div className="col-6 text-right">
                        {invoiceConclusion.isPending
                            ? translate('Invoice reconciliation')
                            : translate('Invoice conclusion')}
                    </div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Time')}</div>
                    <div className="col-6 text-right">
                        {invoiceConclusion.timestamp
                            ? dayjs(invoiceConclusion.timestamp).locale(systemLocale).format('lll')
                            : dayjs().locale(systemLocale).format('lll')}
                    </div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Operator')}</div>
                    <div className="col-6 text-right">{invoiceConclusion.operatorName}</div>
                </div>
                {Object.keys(groupedInvoiceConclusionItemsByClientType).map((clientType, index) => {
                    const invoiceConclusionItems = groupedInvoiceConclusionItemsByClientType[clientType];

                    return (
                        <div key={index}>
                            <div className="custom-list-header mt-32p" style={{ fontSize: '20px' }}>
                                {Number(clientType) === ClientTypeEnum.companyType
                                    ? translate('Legal entity invoices')
                                    : translate('Natural person invoices')}
                            </div>
                            <div>
                                {invoiceConclusionItems.map((it: InvoiceConclusionItemModel, key: number) => {
                                    let totalVat = 0;
                                    if (
                                        (it?.invoiceCount || it?.stornoInvoiceCount) &&
                                        it.invoiceType !== InvoiceTypeEnumText['Proforma invoices']
                                    ) {
                                        return (
                                            <div key={key}>
                                                <br />
                                                <div className="row custom-list-item-report">
                                                    <div className="col-12 text-center">
                                                        <b>{translate(InvoiceTypeEnumText[it.invoiceType])}</b>
                                                    </div>
                                                </div>

                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Cash')}</div>
                                                    <div className="col-6 text-right">
                                                        {numbro(it.cash).format({
                                                            mantissa: 2,
                                                            trimMantissa: false,
                                                            thousandSeparated: true,
                                                        })}{' '}
                                                        {defaultCurrency}
                                                    </div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Card')}</div>
                                                    <div className="col-6 text-right">
                                                        {numbro(it.card).format({
                                                            mantissa: 2,
                                                            trimMantissa: false,
                                                            thousandSeparated: true,
                                                        })}{' '}
                                                        {defaultCurrency}
                                                    </div>
                                                </div>

                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Virman')}</div>
                                                    <div className="col-6 text-right">
                                                        {numbro(it.virman).format({
                                                            mantissa: 2,
                                                            trimMantissa: false,
                                                            thousandSeparated: true,
                                                        })}{' '}
                                                        {defaultCurrency}
                                                    </div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Proforma')}</div>
                                                    <div className="col-6 text-right">
                                                        {numbro(it.proformaDispatched).format({
                                                            mantissa: 2,
                                                            trimMantissa: false,
                                                            thousandSeparated: true,
                                                        })}{' '}
                                                        {defaultCurrency}
                                                    </div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Total revenue')}</div>
                                                    <div className="col-6 text-right">
                                                        {numbro(it.total).format({
                                                            mantissa: 2,
                                                            trimMantissa: false,
                                                            thousandSeparated: true,
                                                        })}{' '}
                                                        {defaultCurrency}
                                                    </div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Total revenue in cash')}</div>
                                                    <div className="col-6 text-right">
                                                        {numbro(it.cashTotal).format({
                                                            mantissa: 2,
                                                            trimMantissa: false,
                                                            thousandSeparated: true,
                                                        })}{' '}
                                                        {defaultCurrency}
                                                    </div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">
                                                        {translate('Total revenue in non cash')}
                                                    </div>
                                                    <div className="col-6 text-right">
                                                        {numbro(it.nonCashTotal).format({
                                                            mantissa: 2,
                                                            trimMantissa: false,
                                                            thousandSeparated: true,
                                                        })}{' '}
                                                        {defaultCurrency}
                                                    </div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Invoice count')}</div>
                                                    <div className="col-6 text-right">{it.invoiceCount}</div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-6">{translate('Storno invoice count')}</div>
                                                    <div className="col-6 text-right">{it.stornoInvoiceCount}</div>
                                                </div>
                                                <br />
                                                <div className="row custom-list-item-report">
                                                    <div className="col-12">
                                                        <b>{translate('Tax recapitulation')}</b>
                                                    </div>
                                                </div>
                                                <div className="row custom-list-item-report">
                                                    <div className="col-3">
                                                        <b>{translate('Tax group')}</b>
                                                    </div>
                                                    <div className="col-3 text-right">
                                                        <b>{translate('Basis')}</b>
                                                    </div>
                                                    <div className="col-3  text-right">
                                                        <b>{translate('Tax')}</b>
                                                    </div>
                                                    <div className="col-3  text-right">
                                                        <b>{translate('Tax2')}</b>
                                                    </div>
                                                </div>
                                                {it?.taxRecapitulation?.map((tax, key) => {
                                                    totalVat += tax.totalTaxValue;
                                                    return (
                                                        <div key={key}>
                                                            <div className="row custom-list-item-report">
                                                                <div className="col-3">{translate(tax.taxGroup)}</div>
                                                                <div className="col-3  text-right">
                                                                    {numbro(tax.base).format({
                                                                        mantissa: 2,
                                                                        trimMantissa: false,
                                                                        thousandSeparated: true,
                                                                    })}{' '}
                                                                    {defaultCurrency}
                                                                </div>
                                                                <div className="col-3  text-right">
                                                                    {numbro(tax.taxValue1).format({
                                                                        mantissa: 2,
                                                                        trimMantissa: false,
                                                                        thousandSeparated: true,
                                                                    })}{' '}
                                                                    {defaultCurrency}
                                                                </div>
                                                                <div className="col-3 text-right">
                                                                    {numbro(tax.taxValue2).format({
                                                                        mantissa: 2,
                                                                        trimMantissa: false,
                                                                        thousandSeparated: true,
                                                                    })}{' '}
                                                                    {defaultCurrency}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div className="row custom-list-item-report">
                                                    <div className="col-3">
                                                        <b>{translate('Total VAT')}</b>
                                                    </div>
                                                    <div className="col-3 text-right"></div>
                                                    <div className="col-3  text-right"></div>
                                                    <div className="col-3  text-right">
                                                        <b>
                                                            {' '}
                                                            {numbro(totalVat).format({
                                                                mantissa: 2,
                                                                trimMantissa: false,
                                                                thousandSeparated: true,
                                                            })}{' '}
                                                            {defaultCurrency}
                                                        </b>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                            </div>
                        </div>
                    );
                })}

                {/* Total invoiced revenue - objedinjeni pregled */}
                <div className="custom-list-header mt-32p" style={{ fontSize: '20px' }}>
                    {translate('Total invoiced revenue')}
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Cash')}</div>
                    <div className="col-6 text-right">
                        {numbro(totalSummary.cash).format({
                            mantissa: 2,
                            trimMantissa: false,
                            thousandSeparated: true,
                        })}{' '}
                        {defaultCurrency}
                    </div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Card')}</div>
                    <div className="col-6 text-right">
                        {numbro(totalSummary.card).format({
                            mantissa: 2,
                            trimMantissa: false,
                            thousandSeparated: true,
                        })}{' '}
                        {defaultCurrency}
                    </div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Virman')}</div>
                    <div className="col-6 text-right">
                        {numbro(totalSummary.virman).format({
                            mantissa: 2,
                            trimMantissa: false,
                            thousandSeparated: true,
                        })}{' '}
                        {defaultCurrency}
                    </div>
                </div>
                {hasDiscountData && (
                    <div className="row custom-list-item-report">
                        <div className="col-6"><b>{translate('Revenue without discount')}</b></div>
                        <div className="col-6 text-right">
                            <b>{numbro(detailsTotalWithoutDiscount).format({ mantissa: 2, trimMantissa: false, thousandSeparated: true })} {defaultCurrency}</b>
                        </div>
                    </div>
                )}
                {hasDiscountData && (
                    <div className="row custom-list-item-report">
                        <div className="col-6"><b>{translate('Total discount')}</b></div>
                        <div className="col-6 text-right">
                            <b>{numbro(totalDiscount).format({ mantissa: 2, trimMantissa: false, thousandSeparated: true })} {defaultCurrency}</b>
                        </div>
                    </div>
                )}
                <div className="row custom-list-item-report" style={{ backgroundColor: '#2d3748', color: 'white' }}>
                    <div className="col-6">
                        <b>{translate('Grand total')}</b>
                    </div>
                    <div className="col-6 text-right">
                        <b>
                            {numbro(totalSummary.total).format({
                                mantissa: 2,
                                trimMantissa: false,
                                thousandSeparated: true,
                            })}{' '}
                            {defaultCurrency}
                        </b>
                    </div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Invoice count')}</div>
                    <div className="col-6 text-right">{totalSummary.invoiceCount}</div>
                </div>
                <div className="row custom-list-item-report">
                    <div className="col-6">{translate('Storno invoice count')}</div>
                    <div className="col-6 text-right">{totalSummary.stornoInvoiceCount}</div>
                </div>

                {/* Blagajnički saldo */}
                {/* {invoiceConclusion.cashDeposit !== null && invoiceConclusion.cashDeposit !== undefined && (
                    <div>
                        <div className="custom-list-header mt-32p" style={{ fontSize: '20px' }}>
                            {translate('Cash register')}
                        </div>
                        <div className="row custom-list-item-report">
                            <div className="col-6">{translate('Cash deposit')}</div>
                            <div className="col-6 text-right">
                                {numbro(invoiceConclusion.cashDeposit || 0).format({ mantissa: 2, trimMantissa: false, thousandSeparated: true })} {defaultCurrency}
                            </div>
                        </div>
                        <div className="row custom-list-item-report">
                            <div className="col-6">{translate('Revenue cash and card')}</div>
                            <div className="col-6 text-right">
                                {numbro(totalSummary.cash + totalSummary.card).format({ mantissa: 2, trimMantissa: false, thousandSeparated: true })} {defaultCurrency}
                            </div>
                        </div>
                        <div className="row custom-list-item-report">
                            <div className="col-6">{translate('To withdraw')}</div>
                            <div className="col-6 text-right">
                                {numbro(totalSummary.cash + totalSummary.card).format({ mantissa: 2, trimMantissa: false, thousandSeparated: true })} {defaultCurrency}
                            </div>
                        </div>
                        <div className="row custom-list-item-report" style={{ backgroundColor: '#2d748f', color: 'white' }}>
                            <div className="col-6"><b>{translate('Remaining in register')}</b></div>
                            <div className="col-6 text-right">
                                <b>
                                    {numbro(invoiceConclusion.cashDeposit || 0).format({ mantissa: 2, trimMantissa: false, thousandSeparated: true })} {defaultCurrency}
                                </b>
                            </div>
                        </div>
                    </div>
                )} */}

                {/* Proforma dispatched (otpremnice) - neupadljivo */}
                {hasProformaDispatched && (
                    <div style={{ opacity: 0.6, marginTop: '24px' }}>
                        <div className="row custom-list-item-report" style={{ backgroundColor: '#e2e8f0' }}>
                            <div className="col-12 text-center">
                                <small><b>{translate('Proforma dispatched invoices')} ({translate('not included in total')})</b></small>
                            </div>
                        </div>
                        <div className="row custom-list-item-report">
                            <div className="col-6"><small>{translate('Total')}</small></div>
                            <div className="col-6 text-right">
                                <small>
                                    {numbro(proformaDispatchedSummary.total).format({
                                        mantissa: 2,
                                        trimMantissa: false,
                                        thousandSeparated: true,
                                    })}{' '}
                                    {defaultCurrency}
                                </small>
                            </div>
                        </div>
                        <div className="row custom-list-item-report">
                            <div className="col-6"><small>{translate('Invoice count')}</small></div>
                            <div className="col-6 text-right">
                                <small>{proformaDispatchedSummary.invoiceCount}</small>
                            </div>
                        </div>
                        <div className="row custom-list-item-report">
                            <div className="col-6"><small>{translate('Storno invoice count')}</small></div>
                            <div className="col-6 text-right">
                                <small>{proformaDispatchedSummary.stornoInvoiceCount}</small>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InvoiceConclusionReport;